import {
  AfterViewInit,
  Component,
  ElementRef,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';

import { Observable, Subject, Subscription, distinctUntilChanged, map } from 'rxjs';

import { GlobalBookingTravelClass } from '@fcom/core/interfaces/global-booking-travel-class.interface';
import { RootPaths } from '@fcom/core/constants';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { FinnairMultiCitySearchInterface } from '@fcom/common/interfaces/flight-search.interface';

const TIMATIC_SCRIPT_SRC = 'https://widget.timatic.iata.org/scripts/v2/iata-timatic-widget-2.0.0.js';
const TIMATIC_STYLE_HREF = 'https://widget.timatic.iata.org/scripts/v2/iata-timatic-style-2.0.0.css';
const TIMATIC_SUPPORTED_LANGUAGES = ['EN', 'DE', 'ES', 'FR', 'PT', 'TR', 'ZH', 'JA'];
const TIMATIC_DEFAULT_LANGUAGE = 'EN';
const ROUND_TRIP_FIELDS = {
  origin: '#departure-roundtrip-search-input',
  destination: '#arrival-roundtrip-search-input',
};
const ONE_WAY_FIELDS = { origin: '#departure-search-input', destination: '#arrival-search-input' };
const FLIGHT_SELECTION_PATH = 'flight-selection';
const DEPARTURE_DAYS_FROM_TODAY = 7;
const DEFAULT_STAY_DAYS = 7;

interface TimaticWindow extends Window {
  timaticWidgetLanguage?: { languageCode: string | null };
}

interface TimaticTripSelection {
  origin: string;
  destination: string;
  isRoundTrip: boolean;
}

@Component({
  selector: 'fin-travel-restrictions',
  templateUrl: './travel-restrictions.component.html',
  styleUrls: ['./travel-restrictions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TravelRestrictionsComponent implements AfterViewInit, OnDestroy {
  private languageService = inject(LanguageService);
  private ref = inject(ElementRef);
  private renderer = inject(Renderer2);
  private platform = inject(PLATFORM_ID);
  private mediaQueryService = inject(MediaQueryService);

  @ViewChild('widget', { static: false }) widget;

  flightsPageRedirectionUrl$: Observable<string | null>;
  readonly isMobile = this.mediaQueryService.isBreakpoint('mobile');
  private readonly isMobile$ = toObservable(this.isMobile);

  private timaticTripSelection$ = new Subject<TimaticTripSelection | null>();
  private stylesheet: HTMLLinkElement | null = null;
  private subscriptions: Subscription = new Subscription();
  private widgetChangeScheduled = false;
  private pendingIsMobile: boolean | null = null;

  constructor() {
    this.flightsPageRedirectionUrl$ = this.timaticTripSelection$.pipe(
      map((selection) => (selection ? this.createRedirectionUrl(selection) : null))
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformServer(this.platform)) {
      return;
    }
    this.setWidgetLanguage();
    this.injectStylesheet();
    this.injectScript();
    this.widget.nativeElement.addEventListener('click', this.onResultAccordionHeaderClick, true);
    this.widget.nativeElement.addEventListener('keydown', this.onResultAccordionHeaderKeydown, true);
    this.subscriptions.add(
      this.isMobile$.pipe(distinctUntilChanged()).subscribe((isMobile) => this.reconcileAccordionState(isMobile))
    );
  }

  ngOnDestroy(): void {
    this.widget?.nativeElement?.removeEventListener('click', this.onResultAccordionHeaderClick, true);
    this.widget?.nativeElement?.removeEventListener('keydown', this.onResultAccordionHeaderKeydown, true);
    this.stylesheet?.remove();
    unsubscribe(this.subscriptions);
  }

  private onResultAccordionHeaderClick = (event: Event): void => {
    const header = (event.target as HTMLElement).closest('.p-accordion-header');
    if (!header) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    const tab = header.closest('.p-accordion-tab');
    const collapsed = tab?.classList.toggle('fin-collapsed');
    this.syncAriaExpanded(tab, collapsed === false);
  };

  private onResultAccordionHeaderKeydown = (event: KeyboardEvent): void => {
    if (event.code === 'Enter' || event.code === 'Space') {
      this.onResultAccordionHeaderClick(event);
    }
  };

  private syncAriaExpanded(tab: Element | null | undefined, expanded: boolean): void {
    tab?.querySelector('.p-accordion-header-link')?.setAttribute('aria-expanded', String(expanded));
    const content = tab?.querySelector('.p-toggleable-content');
    content?.setAttribute('aria-hidden', String(!expanded));
    content?.toggleAttribute('inert', !expanded);
  }

  onWidgetChange(isMobile: boolean | null): void {
    this.pendingIsMobile = isMobile;
    if (this.widgetChangeScheduled) {
      return;
    }
    this.widgetChangeScheduled = true;
    queueMicrotask(() => {
      this.widgetChangeScheduled = false;
      this.readWidgetState(this.pendingIsMobile);
    });
  }

  private readWidgetState(isMobile: boolean | null): void {
    this.timaticTripSelection$.next(this.parseTripSelectionFromWidget());
    this.reconcileAccordionState(isMobile);
  }

  private reconcileAccordionState(isMobile: boolean | null): void {
    const root: Element | undefined = this.widget?.nativeElement;
    if (!root) {
      return;
    }
    const breakpoint = isMobile ? 'mobile' : 'desktop';
    Array.from(root.querySelectorAll('.p-accordion .p-accordion-tab'))
      .filter((tab) => tab.getAttribute('data-fin-accordion-breakpoint') !== breakpoint)
      .forEach((tab) => {
        tab.setAttribute('data-fin-accordion-breakpoint', breakpoint);
        const collapsed = !!isMobile && !tab.classList.contains('p-accordion-tab-active');
        tab.classList.toggle('fin-collapsed', collapsed);
        this.syncAriaExpanded(tab, !collapsed);
      });
  }

  private createRedirectionUrl({ origin, destination, isRoundTrip }: TimaticTripSelection): string {
    const departureDate = LocalDate.now().plusDays(DEPARTURE_DAYS_FROM_TODAY);
    const flights: FinnairMultiCitySearchInterface['flights'] = [
      { origin, destination, departureDate: departureDate.toString() },
    ];
    if (isRoundTrip) {
      flights.push({
        origin: destination,
        destination: origin,
        departureDate: departureDate.plusDays(DEFAULT_STAY_DAYS).toString(),
      });
    }
    const search: FinnairMultiCitySearchInterface = { flights, cabin: GlobalBookingTravelClass.MIXED, adults: 1 };

    return `/${this.languageService.langValue}/${RootPaths.BOOKING_ROOT}/${FLIGHT_SELECTION_PATH}?json=${encodeURIComponent(JSON.stringify(search))}`;
  }

  private parseTripSelectionFromWidget(): TimaticTripSelection | null {
    const roundTripShown = this.isFieldShown(ROUND_TRIP_FIELDS.origin);
    const oneWayShown = this.isFieldShown(ONE_WAY_FIELDS.origin);

    if (roundTripShown !== oneWayShown) {
      const visible = this.parseFieldPair(roundTripShown ? ROUND_TRIP_FIELDS : ONE_WAY_FIELDS);
      return visible ? { ...visible, isRoundTrip: roundTripShown } : null;
    }

    const roundTrip = this.parseFieldPair(ROUND_TRIP_FIELDS);
    if (roundTrip) {
      return { ...roundTrip, isRoundTrip: true };
    }
    const oneWay = this.parseFieldPair(ONE_WAY_FIELDS);
    return oneWay ? { ...oneWay, isRoundTrip: false } : null;
  }

  private parseFieldPair(fields: {
    origin: string;
    destination: string;
  }): Omit<TimaticTripSelection, 'isRoundTrip'> | null {
    const origin = this.parseLocCodeFromWidget(fields.origin);
    const destination = this.parseLocCodeFromWidget(fields.destination);
    return origin && destination ? { origin, destination } : null;
  }

  private isFieldShown(selector: string): boolean {
    const field = this.widget?.nativeElement?.querySelector(selector)?.closest('.tw2-field');
    return !!field && field.getClientRects().length > 0;
  }

  private parseLocCodeFromWidget(selector: string): string | null {
    const root: Element = this.widget?.nativeElement;
    if (!root) {
      return null;
    }
    for (const input of Array.from(root.querySelectorAll(selector))) {
      const field: Element | null = input.closest('.tw2-field');
      const code = field?.querySelector('abbr')?.textContent?.trim();
      if (code && /^[A-Z]{3}$/.test(code)) {
        return code;
      }
    }
    return null;
  }

  private setWidgetLanguage(): void {
    const langKey = this.languageService.langKeyValue.toUpperCase();
    const languageCode = TIMATIC_SUPPORTED_LANGUAGES.includes(langKey) ? langKey : TIMATIC_DEFAULT_LANGUAGE;
    (window as unknown as TimaticWindow).timaticWidgetLanguage = { languageCode };
  }

  private injectStylesheet(): void {
    const link: HTMLLinkElement = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = TIMATIC_STYLE_HREF;
    this.stylesheet = link;
    this.renderer.appendChild(document.head, link);
  }

  private injectScript(): void {
    const script: HTMLScriptElement = document.createElement('script');
    script.type = 'text/javascript';
    script.src = TIMATIC_SCRIPT_SRC;
    this.renderer.appendChild(this.ref.nativeElement, script);
  }
}
